import { useLazyGetMissionDataQuery, useLazyGetMissionDocumentsQuery, useUpdateAbortMissionMutation } from "modules/pvs_activities/redux/service/missions";
import { useLazyGetMovementQuery } from "modules/pvs_activities/redux/service/movements";
import { useGetProcessesQuery } from "modules/pvs_activities/redux/service/processes";
import { createContext, useCallback, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { displayNotification } from "helpers";
import { useNavigate } from "react-router-dom";
import { MOVEMENTS_STATUS } from "modules/pvs_activities/constants";

export const MissionContext = createContext();

const defaultProps = {
    mission_id: null,
    children: ""
};

const MissionTasksContext = (rawProps) => {

    const props = { ...defaultProps, ...rawProps };
    const navigate = useNavigate();
    const authData = useSelector(s => s.auth.data);
    const processRes = useGetProcessesQuery();
    const [movementsTrigger, movementsRes] = useLazyGetMovementQuery();
    const [missionTrigger, missionRes] = useLazyGetMissionDataQuery();
    const [missionAbortTrigger, { isLoading: aborting }] = useUpdateAbortMissionMutation();
    const [documentsTrigger, documentsRes] = useLazyGetMissionDocumentsQuery();

    const taskTrigger = () => {
        if (props.missionId && !props.data) {
            movementsTrigger({ missionID: props.missionId, userId: authData.id, formStatus: 1 });
            missionTrigger({ missionId: props.missionId });
        }
    }

    const getDoucments = () => {
        if (props.missionId) {
            documentsTrigger({ missionID: props.missionId, documentTypeID: '' });
        }
    }

    const abortMission = (reason) => {
        if (props.missionId) {
            missionAbortTrigger({ missionId: props.missionId, reason }).then((response) => {
                if (response.error) {
                    displayNotification("error", "Failed to abort mission");
                } else {
                    displayNotification("success", "Successfully aborted mission");
                    taskTrigger();
                }
            });;
        }
    }

    useEffect(() => {
        taskTrigger();
        getDoucments();
    }, [props.missionId]);

    useEffect(() => {
        const interval = setInterval(() => {
            taskTrigger();
        }, 30 * 1000);
        return () => clearInterval(interval);
    }, []);


    const contextValue = useMemo(() => {
        const processData = processRes.isSuccess ? processRes.data : [];
        const movementData = movementsRes.isSuccess ? movementsRes.data : [];
        const missionData = missionRes.isSuccess ? missionRes.data : {};
        const documents = documentsRes.isSuccess ? documentsRes.data : [];
        let isLoading = false;
        if (props.missionId && !props.data) {
            isLoading = missionRes.isLoading && movementsRes.isLoading;
        } else {
            isLoading = props.data?.isLoadingMovements || false;
        }
        return {
            processes: props.data ? props.data.process : processData,
            movements: (props.data ? props.data.movements : movementData).filter(
                (item) => !(item.status !== MOVEMENTS_STATUS.completed && !item.is_enabled)
            ), missionData: props.data ? props.data.missionData : missionData,
            missionManager: missionRes.data?.mission_manager_id,
            missionDelegate: missionRes.data?.mission_delegate_id,
            missionProxyDelegate: missionRes.data?.mission_proxy_delegate_id,
            missionContactPerson: missionRes.data?.mission_contact_person_id,
            showReport: missionRes.data?.show_report,
            taskTrigger: props.data?.taskTrigger ? props.data?.taskTrigger : taskTrigger,
            isLoading,
            abortMission: abortMission,
            isMissionAborted: props.data ? props.data?.missionData?.status === 4 : missionData?.status === 4,
            missionDocuments: documents,
            is_archived: missionData.is_archived ?? false,
            mission_status_id: missionData.mission_status_id
        };
    }, [props.data, processRes, movementsRes, missionRes, taskTrigger, documentsRes]);

    return (
        <MissionContext.Provider value={contextValue}>
            {props.children}
        </MissionContext.Provider>
    );
}

MissionTasksContext.propTypes = {
    missionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    children: PropTypes.any,
};

export default MissionTasksContext;